import { Bar } from 'react-chartjs-2'
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js'
import dayjs from 'dayjs'
import { useSearchParams } from 'react-router-dom'
import type { ScriptableContext } from 'chart.js'

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend)

type MonthlyData = { debt: number; collected: number }
type TotalTransactionFeesPastChartProps = {
  className?: string
  data: Record<string, MonthlyData>
}

export const TotalTransactionFeesPastChart = ({ className, data }: TotalTransactionFeesPastChartProps) => {
  const { __, __n } = useTranslator()
  const [searchParams] = useSearchParams()
  const monthParam = searchParams.get('month')
  const activeMonthIndex = monthParam ? dayjs(monthParam).month() : undefined
  const currentMonthIndex = dayjs().month()

  const labels = Array.from({ length: 12 }, (_, i) => `${i + 1}`)

  const debt = Array.from({ length: 12 }, (_, m) => {
    const key = `${String(m + 1).padStart(2, '0')}`
    const keyDebt = `${String(m + 1)}`

    return data?.[key]?.debt ?? data?.[keyDebt]?.debt ?? 0
  })

  const collected = Array.from({ length: 12 }, (_, m) => {
    const key = `${String(m + 1).padStart(2, '0')}`
    const keyCollected = `${String(m + 1)}`

    return data?.[key]?.collected ?? data?.[keyCollected]?.collected ?? 0
  })

  const backgroundDebt = debt.map((_, i) => {
    if (i === activeMonthIndex) return '#9EC3FF'
    if (i === currentMonthIndex) return 'rgba(53, 81, 242, 0.00)'

    return '#3551F229'
  })

  const borderDebt = debt.map((_, i) => (i === currentMonthIndex ? 'rgba(53, 81, 242, 0.45)' : 'rgba(0,0,0,0)'))
  const borderWidthDebt = debt.map((_, i) => (i === currentMonthIndex ? 2 : 0))

  const backgroundCollected = collected.map((_, i) => {
    if (i === activeMonthIndex) return '#3551F2'
    if (i === currentMonthIndex) return 'rgba(40, 199, 111, 0.00)'

    // For other past months, collected blends into debt background; keep transparent so primary color is debt
    return 'rgba(53, 81, 242, 0.35)'
  })

  return (
    <div className={className}>
      <Bar
        data={{
          labels,
          datasets: [
            {
              label: __('Collected'),
              data: collected,
              backgroundColor: backgroundCollected,
              borderColor: borderDebt,
              borderWidth: borderWidthDebt,
              borderRadius: (ctx: ScriptableContext<'bar'>) => {
                const index = ctx.dataIndex
                const hasDebt = debt[index] > 0
                const hasCollected = collected[index] > 0
                const top = hasDebt ? 0 : 8
                const bottom = hasCollected ? 8 : 0

                return { topLeft: top, topRight: top, bottomLeft: bottom, bottomRight: bottom }
              },
              borderSkipped: false,
              barThickness: 36,
              stack: 'ewa'
            },
            {
              label: __('Debt'),
              data: debt,
              backgroundColor: backgroundDebt,
              borderColor: borderDebt,
              borderWidth: borderWidthDebt,
              borderRadius: (ctx: ScriptableContext<'bar'>) => {
                const index = ctx.dataIndex
                const bottom = collected[index] > 0 ? 0 : 8
                const top = debt[index] > 0 ? 8 : 0

                return { topLeft: top, topRight: top, bottomLeft: bottom, bottomRight: bottom }
              },
              borderSkipped: false,
              barThickness: 36,
              stack: 'ewa'
            }
          ]
        }}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              backgroundColor: '#1E293B',
              titleColor: '#FFFFFF',
              bodyColor: '#FFFFFF',
              displayColors: false,
              padding: 8,
              cornerRadius: 8,
              callbacks: {
                title: items => {
                  const idx = items?.[0]?.dataIndex ?? 0
                  const monthName = dayjs().month(idx).format('MMMM')

                  return monthName
                },
                label: context => {
                  const value = Number((context.parsed as any)?.y ?? 0)
                  const dsLabel = context.dataset.label || ''

                  return `${dsLabel}: ${__n(value)} đ`
                }
              }
            }
          },
          // Only show the hovered segment
          interaction: { mode: 'nearest', intersect: true },
          scales: {
            x: {
              stacked: true,
              grid: { display: false },
              border: { display: false },
              ticks: { color: '#C2C4CC', font: { size: 12 } }
            },
            y: {
              stacked: true,
              beginAtZero: true,
              grid: { color: 'rgba(0,0,0,0.06)', display: false },
              border: { display: false },
              ticks: { display: false }
            }
          },
          layout: { padding: { top: 8 } }
        }}
      />
    </div>
  )
}

export default TotalTransactionFeesPastChart
